import React, { useState } from 'react';
import { CheckCircle, Users, Clock, Trophy, Plus, UserPlus, ChevronDown, Smartphone, Download, Share2, LogIn } from 'lucide-react';
import { useI18n } from '../i18n';
import LanguageSwitcher from './LanguageSwitcher';

export default function LandingPage({ onLogin, onCreateProject, onJoinProject, onPrivacyPolicy, onAbout }) {
  const { t } = useI18n();
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const faqs = [
    { question: t('landing.faq.q1'), answer: t('landing.faq.a1') },
    { question: t('landing.faq.q2'), answer: t('landing.faq.a2') },
    { question: t('landing.faq.q3'), answer: t('landing.faq.a3') },
    { question: t('landing.faq.q4'), answer: t('landing.faq.a4') },
    { question: t('landing.faq.q5'), answer: t('landing.faq.a5') },
    { question: t('landing.faq.q6'), answer: t('landing.faq.a6') },
  ];

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen">
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-2 items-center sm:items-start">
              <LanguageSwitcher />
              <div className="flex items-center gap-2 sm:gap-3">
                <img src="/logo.png" alt="Dojo Mojo" className="h-10 sm:h-14 w-auto" />
                <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-lavender-500 to-lavender-400 bg-clip-text text-transparent">
                  Dojo Mojo
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 justify-center">
              <button
                onClick={onJoinProject}
                className="px-3 py-2 sm:px-6 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-all"
              >
                {t('landing.nav.haveInvite')}
              </button>
              <button
                onClick={onLogin}
                className="px-3 py-2 sm:px-6 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-base font-medium text-gray-800 hover:bg-gray-50 transition-colors border border-gray-200 bg-white"
              >
                {t('landing.nav.login')}
              </button>
              <button
                onClick={onCreateProject}
                className="px-3 py-2 sm:px-6 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-base font-medium bg-gray-900 text-white hover:bg-gray-700 transition-colors flex items-center gap-1 sm:gap-2"
              >
                <Plus size={16} className="sm:w-[18px] sm:h-[18px]" />
                {t('landing.nav.createAccount')}
              </button>
            </div>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-center gap-1 sm:gap-2 overflow-x-auto py-3">
            <a href="#inicio" className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:text-lavender-600 hover:bg-lavender-50 rounded-lg transition-colors whitespace-nowrap">
              {t('landing.nav.home')}
            </a>
            <a href="#como-funciona" className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:text-lavender-600 hover:bg-lavender-50 rounded-lg transition-colors whitespace-nowrap">
              {t('landing.nav.howItWorks')}
            </a>
            <a href="#beneficios" className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:text-lavender-600 hover:bg-lavender-50 rounded-lg transition-colors whitespace-nowrap">
              {t('landing.nav.benefits')}
            </a>
            <a href="#comecar" className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-peach-600 hover:text-peach-700 hover:bg-peach-50 rounded-lg transition-colors whitespace-nowrap font-semibold">
              {t('landing.nav.start')}
            </a>
            <a href="#baixar" className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:text-lavender-600 hover:bg-lavender-50 rounded-lg transition-colors whitespace-nowrap">
              {t('landing.nav.download')}
            </a>
            <a href="#faq" className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:text-lavender-600 hover:bg-lavender-50 rounded-lg transition-colors whitespace-nowrap">
              {t('landing.nav.faq')}
            </a>
          </div>
        </div>
      </nav>

      <section id="inicio" className="max-w-6xl mx-auto px-4 py-12 sm:px-6 sm:py-20 md:py-32">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-block mb-4 sm:mb-6">
            <span className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-peach-100 text-gray-800 text-xs sm:text-sm font-semibold">
              {t('landing.hero.badge')}
            </span>
          </div>

          <h2 className="text-3xl sm:text-5xl md:text-7xl font-bold text-gray-900 mb-6 sm:mb-8 leading-tight px-2">
            {t('landing.hero.title1')}{' '}
            <span className="bg-gradient-to-r from-lavender-500 to-peach-400 bg-clip-text text-transparent">
              {t('landing.hero.titleHighlight')}
            </span>
          </h2>

          <p className="text-base sm:text-xl md:text-2xl text-gray-600 mb-8 sm:mb-12 leading-relaxed max-w-3xl mx-auto px-4">
            {t('landing.hero.description')}
          </p>

          <div className="space-y-3 px-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center">
              <button
                onClick={onCreateProject}
                className="px-6 py-3 sm:px-8 sm:py-4 rounded-lg sm:rounded-xl font-semibold bg-gray-900 text-white hover:bg-gray-700 transition-colors flex items-center justify-center gap-2 text-base sm:text-lg"
              >
                <Plus size={20} className="sm:w-[22px] sm:h-[22px]" />
                {t('landing.hero.ctaCreate')}
              </button>
              <button
                onClick={onJoinProject}
                className="px-6 py-3 sm:px-8 sm:py-4 rounded-lg sm:rounded-xl font-semibold bg-white text-gray-800 hover:bg-gray-50 transition-all border border-gray-200 border border-gray-300 flex items-center justify-center gap-2 text-base sm:text-lg"
              >
                <UserPlus size={20} className="sm:w-[22px] sm:h-[22px]" />
                {t('landing.hero.ctaJoin')}
              </button>
            </div>
            <p className="text-sm text-gray-500 text-center">
              {t('landing.hero.loginPrompt')}{' '}
              <button onClick={onLogin} className="text-lavender-600 hover:text-lavender-700 font-semibold underline">
                {t('landing.hero.loginLink')}
              </button>
            </p>
          </div>
        </div>
      </section>

      <section id="como-funciona" className="max-w-6xl mx-auto px-4 py-12 sm:px-6 sm:py-20">
        <div className="text-center mb-8 sm:mb-16 px-4">
          <h3 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            {t('landing.features.title')}
          </h3>
          <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto">
            {t('landing.features.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-8">
          <div className="bg-white p-6 sm:p-8 rounded-xl sm:rounded-xl border border-gray-200 hover:border border-gray-200 transition-all">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-peach-200 flex items-center justify-center mb-4 sm:mb-6">
              <Users size={24} className="text-lavender-500 sm:w-7 sm:h-7" />
            </div>
            <h4 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
              {t('landing.features.step1Title')}
            </h4>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
              {t('landing.features.step1Body')}
            </p>
          </div>

          <div className="bg-white p-6 sm:p-8 rounded-xl sm:rounded-xl border border-gray-200 hover:border border-gray-200 transition-all">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-peach-200 flex items-center justify-center mb-4 sm:mb-6">
              <Clock size={24} className="text-lavender-500 sm:w-7 sm:h-7" />
            </div>
            <h4 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
              {t('landing.features.step2Title')}
            </h4>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
              {t('landing.features.step2Body')}
            </p>
          </div>

          <div className="bg-white p-6 sm:p-8 rounded-xl sm:rounded-xl border border-gray-200 hover:border border-gray-200 transition-all">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-peach-200 flex items-center justify-center mb-4 sm:mb-6">
              <Trophy size={24} className="text-lavender-500 sm:w-7 sm:h-7" />
            </div>
            <h4 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
              {t('landing.features.step3Title')}
            </h4>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
              {t('landing.features.step3Body')}
            </p>
          </div>
        </div>
      </section>

      <section id="beneficios" className="max-w-6xl mx-auto px-4 py-12 sm:px-6 sm:py-20">
        <div className="bg-gradient-to-br from-lavender-400 to-lavender-500 rounded-xl sm:rounded-xl p-6 sm:p-12 md:p-16 border border-gray-200">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
              {t('landing.benefits.title')}
            </h3>
            <p className="text-base sm:text-xl text-white opacity-90 mb-8 sm:mb-12 leading-relaxed">
              {t('landing.benefits.subtitle')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 text-left">
              <div className="flex gap-3 sm:gap-4">
                <div className="flex-shrink-0">
                  <CheckCircle size={20} className="text-white sm:w-6 sm:h-6" />
                </div>
                <div>
                  <h4 className="text-base sm:text-xl font-bold text-white mb-1 sm:mb-2">
                    {t('landing.benefits.transparencyTitle')}
                  </h4>
                  <p className="text-sm sm:text-base text-white opacity-90">
                    {t('landing.benefits.transparencyBody')}
                  </p>
                </div>
              </div>

              <div className="flex gap-3 sm:gap-4">
                <div className="flex-shrink-0">
                  <CheckCircle size={20} className="text-white sm:w-6 sm:h-6" />
                </div>
                <div>
                  <h4 className="text-base sm:text-xl font-bold text-white mb-1 sm:mb-2">
                    {t('landing.benefits.fairTitle')}
                  </h4>
                  <p className="text-sm sm:text-base text-white opacity-90">
                    {t('landing.benefits.fairBody')}
                  </p>
                </div>
              </div>

              <div className="flex gap-3 sm:gap-4">
                <div className="flex-shrink-0">
                  <CheckCircle size={20} className="text-white sm:w-6 sm:h-6" />
                </div>
                <div>
                  <h4 className="text-base sm:text-xl font-bold text-white mb-1 sm:mb-2">
                    {t('landing.benefits.simpleTitle')}
                  </h4>
                  <p className="text-sm sm:text-base text-white opacity-90">
                    {t('landing.benefits.simpleBody')}
                  </p>
                </div>
              </div>

              <div className="flex gap-3 sm:gap-4">
                <div className="flex-shrink-0">
                  <CheckCircle size={20} className="text-white sm:w-6 sm:h-6" />
                </div>
                <div>
                  <h4 className="text-base sm:text-xl font-bold text-white mb-1 sm:mb-2">
                    {t('landing.benefits.freeTitle')}
                  </h4>
                  <p className="text-sm sm:text-base text-white opacity-90">
                    {t('landing.benefits.freeBody')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="comecar" className="max-w-6xl mx-auto px-4 py-12 sm:px-6 sm:py-20 md:py-32">
        <div className="text-center mb-8">
          <h3 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            {t('landing.start.title')}
          </h3>
          <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto">
            {t('landing.start.subtitle')}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">
          <div className="bg-white rounded-xl p-6 border border-peach-300 hover:shadow-lg transition-all">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-lg bg-peach-400 flex items-center justify-center flex-shrink-0">
                <Plus size={24} className="text-white" />
              </div>
              <h4 className="font-bold text-gray-900 text-lg">{t('landing.start.option1Title')}</h4>
            </div>
            <p className="text-sm text-gray-600 mb-4 leading-relaxed">
              {t('landing.start.option1BodyLead')} <strong>{t('landing.start.option1BodyBold')}</strong> {t('landing.start.option1BodyTail')}
            </p>
            <button
              onClick={onCreateProject}
              className="w-full bg-gray-900 text-white py-3 rounded-lg text-sm font-semibold hover:bg-gray-700 transition-colors"
            >
              {t('landing.start.option1Button')}
            </button>
          </div>

          <div className="bg-white rounded-xl p-6 border border-lavender-300 hover:shadow-lg transition-all">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-lg bg-lavender-400 flex items-center justify-center flex-shrink-0">
                <LogIn size={24} className="text-white" />
              </div>
              <h4 className="font-bold text-gray-900 text-lg">{t('landing.start.option2Title')}</h4>
            </div>
            <p className="text-sm text-gray-600 mb-4 leading-relaxed">
              {t('landing.start.option2BodyLead')} <strong>{t('landing.start.option2BodyBold')}</strong> {t('landing.start.option2BodyTail')}
            </p>
            <button
              onClick={onLogin}
              className="w-full bg-white text-gray-900 border border-gray-200 py-3 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors"
            >
              {t('landing.start.option2Button')}
            </button>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-300 hover:shadow-lg transition-all">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-lg bg-gray-500 flex items-center justify-center flex-shrink-0">
                <UserPlus size={24} className="text-white" />
              </div>
              <h4 className="font-bold text-gray-900 text-lg">{t('landing.start.option3Title')}</h4>
            </div>
            <p className="text-sm text-gray-600 mb-4 leading-relaxed">
              {t('landing.start.option3BodyLead')} <strong>{t('landing.start.option3BodyBold')}</strong> {t('landing.start.option3BodyTail')}
            </p>
            <button
              onClick={onJoinProject}
              className="w-full bg-gray-600 text-white py-3 rounded-xl text-sm font-bold hover:bg-gray-700 transition-all shadow-sm"
            >
              {t('landing.start.option3Button')}
            </button>
          </div>
        </div>
      </section>

      <section id="faq" className="max-w-4xl mx-auto px-4 py-12 sm:px-6 sm:py-20">
        <div className="text-center mb-8 sm:mb-12">
          <h3 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            {t('landing.faq.title')}
          </h3>
          <p className="text-base sm:text-xl text-gray-600">
            {t('landing.faq.subtitle')}
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-lg sm:rounded-xl border border-gray-200 overflow-hidden transition-all"
            >
              <button
                onClick={() => toggleFaq(index)}
                className="w-full px-4 py-4 sm:px-6 sm:py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
              >
                <span className="text-base sm:text-lg font-semibold text-gray-900 pr-4">
                  {faq.question}
                </span>
                <ChevronDown
                  size={20}
                  className={`flex-shrink-0 text-lavender-500 transition-transform duration-200 sm:w-6 sm:h-6 ${
                    openFaqIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>

              <div
                className={`overflow-hidden transition-all duration-200 ${
                  openFaqIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-4 pb-4 sm:px-6 sm:pb-5 pt-0">
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="baixar" className="max-w-6xl mx-auto px-4 py-12 sm:px-6 sm:py-20">
        <div className="bg-white rounded-xl sm:rounded-xl p-6 sm:p-12 border border-gray-200">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 sm:mb-12">
              <div className="inline-flex items-center gap-2 mb-4">
                <Smartphone size={24} className="text-lavender-500 sm:w-7 sm:h-7" />
              </div>
              <h3 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
                {t('landing.download.title')}
              </h3>
              <p className="text-base sm:text-xl text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto">
                {t('landing.download.subtitle')}
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <div className="bg-gray-100 px-6 py-4 rounded-xl flex items-center gap-3 opacity-60 w-full sm:w-auto">
                  <div className="w-10 h-10 bg-gray-300 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-600" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="text-xs text-gray-500">{t('landing.download.comingSoonOn')}</div>
                    <div className="text-base font-bold text-gray-700">{t('landing.download.appStore')}</div>
                  </div>
                </div>

                <div className="bg-gray-100 px-6 py-4 rounded-xl flex items-center gap-3 opacity-60 w-full sm:w-auto">
                  <div className="w-10 h-10 bg-gray-300 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-600" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="text-xs text-gray-500">{t('landing.download.comingSoonOnMasc')}</div>
                    <div className="text-base font-bold text-gray-700">{t('landing.download.googlePlay')}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-8 sm:pt-12">
              <div className="text-center mb-6 sm:mb-8">
                <div className="inline-flex items-center gap-2 mb-3">
                  <Download size={20} className="text-lavender-500 sm:w-6 sm:h-6" />
                </div>
                <h4 className="text-xl sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">
                  {t('landing.download.installTitle')}
                </h4>
                <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
                  {t('landing.download.installSubtitle')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                <div className="bg-lavender-50 border border-lavender-200 rounded-lg sm:rounded-xl p-5 sm:p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-lavender-400 flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
                      </svg>
                    </div>
                    <h5 className="text-base sm:text-lg font-bold text-gray-900">{t('landing.download.iosHeader')}</h5>
                  </div>
                  <ol className="space-y-3 text-sm sm:text-base text-gray-700">
                    <li className="flex gap-2">
                      <span className="font-bold text-lavender-600 flex-shrink-0">1.</span>
                      <span>{t('landing.download.iosStep1Prefix')} <strong>dojomojo.app</strong> {t('landing.download.iosStep1Suffix')}</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-bold text-lavender-600 flex-shrink-0">2.</span>
                      <span>{t('landing.download.iosStep2Prefix')} <Share2 className="inline w-4 h-4 mx-1" /> <strong>{t('landing.download.iosStep2Share')}</strong> {t('landing.download.iosStep2Suffix')}</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-bold text-lavender-600 flex-shrink-0">3.</span>
                      <span>{t('landing.download.iosStep3Prefix')} <strong>{t('landing.download.iosStep3Action')}</strong></span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-bold text-lavender-600 flex-shrink-0">4.</span>
                      <span>{t('landing.download.iosStep4Prefix')} <strong>{t('landing.download.iosStep4Action')}</strong> {t('landing.download.iosStep4Suffix')}</span>
                    </li>
                  </ol>
                </div>

                <div className="bg-peach-100 border border-peach-300 rounded-lg sm:rounded-xl p-5 sm:p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-peach-400 flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                      </svg>
                    </div>
                    <h5 className="text-base sm:text-lg font-bold text-gray-900">{t('landing.download.androidHeader')}</h5>
                  </div>
                  <ol className="space-y-3 text-sm sm:text-base text-gray-700">
                    <li className="flex gap-2">
                      <span className="font-bold text-peach-500 flex-shrink-0">1.</span>
                      <span>{t('landing.download.androidStep1Prefix')} <strong>dojomojo.app</strong> {t('landing.download.androidStep1Suffix')}</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-bold text-peach-500 flex-shrink-0">2.</span>
                      <span>{t('landing.download.androidStep2Prefix')} <strong>{t('landing.download.androidStep2Icon')}</strong> {t('landing.download.androidStep2Suffix')}</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-bold text-peach-500 flex-shrink-0">3.</span>
                      <span>{t('landing.download.androidStep3Prefix')} <strong>{t('landing.download.androidStep3ActionA')}</strong> {t('landing.download.androidStep3Or')} <strong>{t('landing.download.androidStep3ActionB')}</strong></span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-bold text-peach-500 flex-shrink-0">4.</span>
                      <span>{t('landing.download.androidStep4Prefix')} <strong>{t('landing.download.androidStep4ActionA')}</strong> {t('landing.download.androidStep4Or')} <strong>{t('landing.download.androidStep4ActionB')}</strong></span>
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-gray-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-6 sm:px-6 sm:py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
            <div className="flex items-center gap-2 sm:gap-3">
              <img src="/logo.png" alt="Dojo Mojo" className="h-8 sm:h-10 w-auto" />
              <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-lavender-500 to-lavender-400 bg-clip-text text-transparent">
                Dojo Mojo
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6">
              <div className="flex items-center gap-4">
                <button
                  onClick={onAbout}
                  className="text-xs sm:text-sm text-gray-600 hover:text-lavender-600 font-medium transition-colors"
                >
                  {t('landing.footer.about')}
                </button>
                <button
                  onClick={onPrivacyPolicy}
                  className="text-xs sm:text-sm text-gray-600 hover:text-lavender-600 font-medium transition-colors"
                >
                  {t('landing.footer.privacy')}
                </button>
              </div>
              <p className="text-gray-600 text-xs sm:text-sm text-center">
                {t('landing.footer.copyright')}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
